import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, Car, User, ShieldCheck } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { useAuth } from "@/contexts/AuthContext";
import { useFirebase } from "@/contexts/FirebaseContext";
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";

const Login = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  
  const urlParams = new URLSearchParams(location.search);
  const role = urlParams.get("role") || "student";

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  let auth;
  let firebase;
  
  try {
    auth = useAuth();
    firebase = useFirebase();
  } catch (error) {
    console.error("Authentication context error:", error);
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
          <div className="max-w-md mx-auto px-4 sm:px-6">
            <motion.div 
              className="bg-white rounded-lg shadow-xl overflow-hidden border border-red-100"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              <div className="px-6 py-8 text-center">
                <h1 className="text-2xl font-bold text-road-900">Service Unavailable</h1>
                <p className="text-road-600 mt-2">
                  The authentication service is currently unavailable. Please try again later.
                </p>
                <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={() => window.location.reload()}>
                  Refresh Page
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </Layout>
    );
  }
  
  const { toast } = useToast();

  useEffect(() => {
    if (auth?.user) {
      switch (auth.user.role) {
        case "student":
          navigate("/student-dashboard");
          break;
        case "instructor":
          navigate("/instructor-dashboard");
          break;
        case "admin":
          navigate("/admin-dashboard");
          break;
      }
    }
  }, [auth?.user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    
    if (!auth || !firebase) {
      setError("Authentication system is not available. Please try again later.");
      return;
    }
    
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setIsLoading(true);
    
    try {
      console.log("Checking if user exists:", email, "with role:", role);
      const userDoc = await firebase.getUserByEmail(email);
      console.log("User document retrieved:", userDoc);
      
      if (!userDoc) {
        if (role === 'student') {
          // For students, we can create the account if it doesn't exist during demo mode
          console.log("No student account found, will try to login anyway");
        } else {
          setError(`No ${role} account found with this email`);
          setIsLoading(false);
          return;
        }
      } else {
        // Check if user has account
        if (userDoc.hasAccount === false && role === 'student') {
          setError("You have a booking but no account. Please create an account to access your dashboard.");
          toast({
            variant: "destructive",
            title: "Account Required",
            description: "You have a booking but no account. Would you like to create one?",
          });
          setIsLoading(false);
          // TODO: Could redirect to account creation page with pre-filled data
          return;
        }
        
        if (userDoc.role && userDoc.role !== role) {
          setError(`This account is registered as a ${userDoc.role}, not a ${role}`);
          setIsLoading(false);
          return;
        }
      }
      
      // Login the user
      console.log("Attempting to login user with role:", role);
      const success = await auth.login(email, password, role);
      
      if (success) {
        toast({
          title: "Login successful",
          description: "Welcome back!",
        });
        
        // User redirection will be handled by the useEffect above
      } else {
        setError("Invalid email or password");
        toast({
          variant: "destructive",
          title: "Login failed",
          description: "Invalid email or password",
        });
      }
    } catch (err) {
      console.error("Login error:", err);
      setError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleContent = () => {
    switch (role) {
      case "instructor":
        return {
          title: "Instructor Login",
          icon: <Car className="h-8 w-8 text-drivewise-600" />,
          description: "Access your schedule, manage your students, and update your profile.",
        };
      case "admin":
        return {
          title: "Admin Login",
          icon: <ShieldCheck className="h-8 w-8 text-drivewise-600" />,
          description: "Manage your driving school operations, instructors, students, and more.",
        };
      case "student":
      default:
        return {
          title: "Student Login",
          icon: <User className="h-8 w-8 text-drivewise-600" />,
          description: "Book lessons, track your progress, and access learning materials.",
        };
    }
  };

  const roleContent = getRoleContent();

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <motion.div 
            className="bg-white rounded-lg shadow-xl overflow-hidden border border-blue-100"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}  
          >
            <div className="px-6 py-8">
              <motion.div 
                className="text-center mb-8"
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-gradient-to-br from-drivewise-100 to-blue-100 h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  {roleContent.icon}
                </div>
                <h1 className="text-2xl font-bold text-road-900 bg-clip-text text-transparent bg-gradient-to-r from-drivewise-600 to-blue-600">{roleContent.title}</h1>
                <p className="text-road-600 mt-2">{roleContent.description}</p>
              </motion.div>

              {error && (
                <motion.div 
                  className="mb-6 bg-red-50 text-red-800 p-3 rounded-md text-sm border-l-4 border-red-500"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {error}
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    required
                    className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link to="/forgot-password" className="text-sm text-drivewise-600 hover:text-drivewise-800 hover:underline">
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      required
                      className="border-gray-300 focus:border-drivewise-500 focus:ring focus:ring-drivewise-200 transition-all"
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-5 w-5" />
                      ) : (
                        <Eye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-drivewise-600 to-blue-600 hover:from-drivewise-700 hover:to-blue-700 text-white"
                    disabled={isLoading}
                  >
                    {isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </motion.div>
              </form>

              <div className="mt-8 text-center">
                <p className="text-road-600">
                  Don't have an account?{" "}
                  <Link to="/register" className="text-drivewise-600 hover:text-drivewise-800 font-medium hover:underline">
                    Register now
                  </Link>
                </p>
              </div>
              
              {role !== "student" && (
                <div className="mt-4 text-center">
                  <Link to="/login?role=student" className="text-drivewise-600 hover:text-drivewise-800 text-sm hover:underline">
                    Student Login
                  </Link>
                  {role !== "instructor" && (
                    <span className="text-gray-400 mx-2">•</span>
                  )}
                  {role !== "instructor" && (
                    <Link to="/login?role=instructor" className="text-drivewise-600 hover:text-drivewise-800 text-sm hover:underline">
                      Instructor Login
                    </Link>
                  )}
                  {role !== "admin" && (
                    <span className="text-gray-400 mx-2">•</span>
                  )}
                  {role !== "admin" && (
                    <Link to="/login?role=admin" className="text-drivewise-600 hover:text-drivewise-800 text-sm hover:underline">
                      Admin Login
                    </Link>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default Login;
